const GLOBAL_SCOPE = '*'
const MAX_ALIAS_HOPS = 12

type AliasMap = Map<string, string>

type FallbackStrategy = 'alias' | 'route_label_role' | 'container'

const scopedAliases = new Map<string, AliasMap>()
const aliasMetrics = {
  aliasHits: 0,
  fallbackMatches: 0,
  fallbackByStrategy: {
    alias: 0,
    route_label_role: 0,
    container: 0
  } as Record<FallbackStrategy, number>
}

const normalizeAliasId = (value: string | null | undefined) => {
  if (!value) return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

const normalizeScope = (scope?: string | null) => {
  if (!scope) return GLOBAL_SCOPE
  const normalized = scope.trim()
  return normalized.length > 0 ? normalized : GLOBAL_SCOPE
}

const getOrCreateScopeMap = (scope: string): AliasMap => {
  const existing = scopedAliases.get(scope)
  if (existing) return existing
  const created: AliasMap = new Map()
  scopedAliases.set(scope, created)
  return created
}

const resolveInScope = (sourceId: string, scope: string) => {
  const aliasMap = scopedAliases.get(scope)
  if (!aliasMap) return sourceId

  let current = sourceId
  const seen = new Set<string>()
  let hops = 0
  while (hops < MAX_ALIAS_HOPS) {
    const next = aliasMap.get(current)
    if (!next || next === current || seen.has(next)) {
      break
    }
    seen.add(next)
    current = next
    hops += 1
  }
  return current
}

export const registerTutorialIdAlias = (params: {
  sourceId: string
  resolvedId: string
  scope?: string | null
}) => {
  const sourceId = normalizeAliasId(params.sourceId)
  const resolvedId = normalizeAliasId(params.resolvedId)
  if (!sourceId || !resolvedId || sourceId === resolvedId) return

  const scope = normalizeScope(params.scope)
  const aliasMap = getOrCreateScopeMap(scope)
  aliasMap.set(sourceId, resolvedId)
}

export const resolveTutorialIdAlias = (params: { targetId: string; scope?: string | null }) => {
  const targetId = normalizeAliasId(params.targetId)
  if (!targetId) return null

  const scope = normalizeScope(params.scope)
  const scopedResolved = resolveInScope(targetId, scope)
  if (scopedResolved !== targetId) {
    aliasMetrics.aliasHits += 1
    return scopedResolved
  }

  const globalResolved = resolveInScope(targetId, GLOBAL_SCOPE)
  if (globalResolved !== targetId) {
    aliasMetrics.aliasHits += 1
    return globalResolved
  }

  return null
}

export const recordTutorialFallbackMatch = (strategy: FallbackStrategy) => {
  aliasMetrics.fallbackMatches += 1
  aliasMetrics.fallbackByStrategy[strategy] += 1
}

export const getTutorialAliasMetrics = () => {
  return {
    aliasHits: aliasMetrics.aliasHits,
    fallbackMatches: aliasMetrics.fallbackMatches,
    fallbackByStrategy: { ...aliasMetrics.fallbackByStrategy }
  }
}

export const clearTutorialIdAliases = () => {
  scopedAliases.clear()
  aliasMetrics.aliasHits = 0
  aliasMetrics.fallbackMatches = 0
  aliasMetrics.fallbackByStrategy.alias = 0
  aliasMetrics.fallbackByStrategy.route_label_role = 0
  aliasMetrics.fallbackByStrategy.container = 0
}
